import {
  breadcrumbsIntegration,
  captureConsoleIntegration,
  init,
} from '@sentry/react';

import { MainConfig } from '#/service/config/config.types';
import { SearchParams } from '#/service/utils';

export const initSentry = (config: MainConfig) => {
  const isEnabled =
    config.featureConfig.errorReporting?.enabled ||
    config.featureConfig.crashReporting?.enabled;

  const searchParams = new URLSearchParams(globalThis.location.search);

  // we don't want any development related errors sent to Sentry.
  // this is a wider net than usual, because even prod builds can be used for development purposes.
  const isDev =
    process.env.NODE_ENV === 'development' ||
    process.env.goal === 'local' ||
    globalThis.location.hostname === 'localhost' ||
    globalThis.location.hostname === 'dev.web.one.accedo.tv' ||
    globalThis.location.hostname === 'generic.customer.dev.web.one.accedo.tv' ||
    searchParams.get(SearchParams.Env) === 'dev' ||
    searchParams.has(SearchParams.UiVersion) ||
    searchParams.has(SearchParams.IntegrationVersion);

  if (!isEnabled || isDev) {
    return;
  }

  init({
    release: config.app?.version,
    dsn: config.featureConfig.sentry?.dsn,
    environment: 'production',
    integrations: [
      breadcrumbsIntegration({
        console: false, // disable breadcrumbs for console.log/warn/error etc.
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
      captureConsoleIntegration({ levels: [] }), // do we want to ignore all console log levels, or just a few?
    ],
    sendDefaultPii: true,
    ignoreErrors: [
      // ignore one errors because they are being re-mapped into to the actual error code for reporting.
      /OneError/i,
      ...(config?.featureConfig?.errorReporting?.errorCodes?.ignore || []),
    ],
    beforeSend: (event) => {
      const frames =
        event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ??
        [];

      // Filter out errors coming from GTM-injected code. These run as anonymous inline scripts.
      // Their stack frames show the page root URL (e.g. https://example.com/:1:123), never a `.js` filename.
      // The client app and plugins always have at least one `.js` frame.
      if (
        frames.length > 0 &&
        frames.every((f) => !/\.js(\?|$)/.test(f.filename ?? ''))
      ) {
        return null;
      }

      // Filter out unhandled promise rejections with no stack frames. These are always
      // non-Error values (undefined, string, etc.) rejected by third-party scripts.
      // Our own code only ever rejects with proper Error objects, which always carry a stack.
      // Or if not, that's something to fix, but that's no reason to sends this noise to Sentry.
      const isUnhandledRejection = event.exception?.values?.some((value) =>
        value.mechanism?.type?.endsWith('onunhandledrejection')
      );

      if (isUnhandledRejection && frames.length === 0) {
        return null;
      }

      return event;
    },
    debug: false,
  });
};
